import React from 'react';
import { Box, Tooltip, Badge } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CollectionsIcon from '@mui/icons-material/Collections';

/**
 * Theme colors from the app's design system
 */
const THEME_COLORS = {
  primary: '#232323',
  primaryLight: '#404040',
  secondary: '#9E9E9E',
  background: '#FAFAFA',
  badgeBlue: '#2196F3',
};

export type ViewFilesButtonSize = 'small' | 'medium' | 'large';

export interface ViewFilesButtonProps {
  /** Click handler to open view modal */
  onClick: () => void;
  /** Number of files to display in badge */
  fileCount: number;
  /** Button size */
  size?: ViewFilesButtonSize;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Tooltip text */
  tooltip?: string;
  /** Custom width */
  width?: number | string;
  /** Custom height */
  height?: number | string;
  /** Hide badge and show only when fileCount > 0 */
  hideWhenEmpty?: boolean;
}

/**
 * ViewFilesButton Component
 * 
 * A standalone view button with eye icon and file count badge.
 * Use this in table view modes to show uploaded files count with a click-to-view action.
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <ViewFilesButton 
 *   onClick={() => setViewModalOpen(true)} 
 *   fileCount={3}
 * />
 * 
 * // In a table cell
 * <ViewFilesButton 
 *   onClick={handleViewFiles}
 *   fileCount={files.length}
 *   size="small"
 *   hideWhenEmpty
 * />
 * ```
 */
const ViewFilesButton: React.FC<ViewFilesButtonProps> = ({
  onClick,
  fileCount = 0,
  size = 'medium',
  disabled = false,
  tooltip,
  width,
  height,
  hideWhenEmpty = false,
}) => {
  const { t: tGeneral } = useTranslation('general');
  
  // Use provided tooltip or default to localized text
  const tooltipText = tooltip || tGeneral('viewUploadedFiles');
  
  // Size configurations
  const sizeConfig = {
    small: {
      buttonSize: 36,
      iconSize: 18,
      badgeFontSize: '9px',
      badgeSize: 16,
      borderRadius: 8,
    },
    medium: {
      buttonSize: 44,
      iconSize: 20,
      badgeFontSize: '10px',
      badgeSize: 18,
      borderRadius: 10,
    },
    large: {
      buttonSize: 52,
      iconSize: 24,
      badgeFontSize: '11px',
      badgeSize: 20,
      borderRadius: 12,
    },
  };

  const config = sizeConfig[size];

  // Hide component if no files and hideWhenEmpty is true
  if (hideWhenEmpty && fileCount === 0) {
    return null;
  }

  // Show dash or placeholder when no files
  if (fileCount === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: THEME_COLORS.secondary,
          fontFamily: 'Nunito, sans-serif',
          fontSize: '14px',
        }}
      >
        —
      </Box>
    );
  }

  return (
    <Tooltip title={`${tooltipText} (${fileCount})`} arrow placement="top">
      <Badge
        badgeContent={fileCount}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: THEME_COLORS.badgeBlue,
            color: '#FFFFFF',
            fontSize: config.badgeFontSize,
            fontWeight: 700,
            minWidth: config.badgeSize,
            width: config.badgeSize,
            height: config.badgeSize,
            borderRadius: '50%',
            top: 2,
            right: 2,
            border: '2px solid #FFFFFF',
            fontFamily: 'Nunito, sans-serif',
            padding: 0,
          },
        }}
      >
        <Box
          onClick={disabled ? undefined : onClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: width ?? config.buttonSize,
            height: height ?? config.buttonSize,
            borderRadius: `${config.borderRadius}px`,
            backgroundColor: disabled ? '#F5F5F5' : THEME_COLORS.background,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            border: '1px solid #E0E0E0',
            '&:hover': disabled ? {} : {
              backgroundColor: '#F0F0F0',
              borderColor: THEME_COLORS.primaryLight,
            },
            '&:active': disabled ? {} : {
              backgroundColor: '#E8E8E8',
              transform: 'scale(0.98)',
            },
          }}
        >
          <CollectionsIcon 
            sx={{ 
              fontSize: config.iconSize,
              color: disabled ? THEME_COLORS.secondary : THEME_COLORS.primary,
            }} 
          />
        </Box>
      </Badge>
    </Tooltip>
  );
};

export default ViewFilesButton;
